/**
 * シナリオエンジン。
 *
 * 章のロード、目標の進捗、クリア判定、仮説と対処、ヒント、セーブ／ロードを担当する。
 * 会話文や条件の「中身」は一切持たず、すべて JSON から受け取る。
 */

import type { DeviceId, NetworkWorld } from '../types/network';
import type {
  Chapter,
  Clue,
  Dialogue,
  Effect,
  Fix,
  GameState,
  Hypothesis,
  Interactable,
  InteractionOutcome,
  KnowledgeCard,
  Objective,
} from '../types/scenario';
import { cloneWorld } from '../sim/world';
import { migrateAddressPlan } from '../sim/addressPlan';
import { evaluateCondition } from './conditions';
import { applyEffects, emptyOutcome, mergeOutcome, type EffectOutcome } from './effects';
import { Emitter } from './events';
import { indexById, type GameData, type GlossaryEntry } from './dataSource';
import { createInitialState, ensureChapterStats, loadGame, saveGame } from './state';

export interface ObjectiveProgress {
  objective: Objective;
  done: boolean;
  visible: boolean;
}

/** applyFix が対処を実行しなかった理由 */
export type FixBlockReason =
  /** その ID の対処が今の章に無い */
  | 'not-found'
  /** どの仮説からも解放されていない（＝手帳に出ていない） */
  | 'not-unlocked'
  /** 対処自身の condition が偽（＝前の手順がまだ済んでいない） */
  | 'condition-not-met';

export interface ApplyFixResult {
  /** 見つかった対処。ID 自体が無ければ null */
  fix: Fix | null;
  effects: EffectOutcome;
  /** 効果を実際に適用したか */
  applied: boolean;
  /** 適用しなかった理由（適用したなら null） */
  blockedReason: FixBlockReason | null;
}

export interface HintState {
  /** 今のヒント対象（未達成の目標に対応するもの） */
  available: boolean;
  /** 何段階目まで公開されたか（0 = まだ何も見ていない） */
  revealed: number;
  totalLevels: number;
  /** 公開済みのヒント文 */
  texts: string[];
  /** 一定時間進展が無く、ポートから声を掛けてよい状態か */
  offered: boolean;
}

export interface EngineEvents extends Record<string, unknown> {
  'state-changed': void;
  'world-changed': void;
  clue: string;
  card: string;
  sound: string;
  dialogue: string;
  description: { text: string; terms?: string[] };
  'chapter-started': Chapter;
  'chapter-cleared': Chapter;
  hint: HintState;
}

/** ヒントを自動で提案するまでの無進展時間（秒） */
const HINT_IDLE_SECONDS = 150;

export class ScenarioEngine {
  readonly events = new Emitter<EngineEvents>();

  readonly data: GameData;
  /** 章のセットアップで書き換わるため、元データとは別の実体を持つ */
  world: NetworkWorld;
  state: GameState;

  private readonly pristineWorld: NetworkWorld;
  private readonly chaptersById: Record<string, Chapter>;
  private readonly dialoguesById: Record<string, Dialogue>;
  private readonly cardsById: Record<string, KnowledgeCard>;
  private readonly interactablesById: Record<string, Interactable>;

  private hintRevealed = new Map<string, number>();
  private idleSeconds = 0;
  private lastProgressSignature = '';
  private chapterStartedAt = Date.now();
  private clearedChapters = new Set<string>();

  constructor(data: GameData, playerName = '新人') {
    this.data = data;
    this.pristineWorld = cloneWorld(data.world);
    this.world = cloneWorld(data.world);
    this.chaptersById = indexById(data.chapters);
    this.dialoguesById = indexById(data.dialogues);
    this.cardsById = indexById(data.knowledgeCards);
    this.interactablesById = indexById(data.interactables);
    const first = data.chapters[0];
    this.state = createInitialState(playerName, first ? first.id : '');
  }

  // ------------------------------------------------------------
  // 章
  // ------------------------------------------------------------

  get chapter(): Chapter | null {
    return this.chaptersById[this.state.currentChapterId] ?? null;
  }

  get chapters(): Chapter[] {
    return this.data.chapters;
  }

  getChapter(id: string): Chapter | null {
    return this.chaptersById[id] ?? null;
  }

  /** 章を開始する。setup を適用し、ブリーフィング会話を返す */
  startChapter(chapterId: string): Dialogue | null {
    const chapter = this.chaptersById[chapterId];
    if (!chapter) {
      console.warn(`[scenario] 章 ${chapterId} が見つかりません`);
      return null;
    }
    this.state.currentChapterId = chapterId;
    ensureChapterStats(this.state, chapterId);
    this.chapterStartedAt = Date.now();
    this.idleSeconds = 0;
    this.hintRevealed.clear();

    this.runEffects(chapter.setup);
    this.events.emit('chapter-started', chapter);
    this.events.emit('state-changed', undefined);
    return this.dialoguesById[chapter.briefingDialogueId] ?? null;
  }

  /** 進行に必要な最低限の再開処理（セーブから復帰した場合） */
  resumeChapter(): void {
    const chapter = this.chapter;
    if (!chapter) return;
    ensureChapterStats(this.state, chapter.id);
    this.chapterStartedAt = Date.now();
    this.events.emit('chapter-started', chapter);
  }

  /** 次の章 */
  nextChapter(): Chapter | null {
    const index = this.data.chapters.findIndex((c) => c.id === this.state.currentChapterId);
    return index >= 0 ? (this.data.chapters[index + 1] ?? null) : null;
  }

  // ------------------------------------------------------------
  // 目標とクリア判定
  // ------------------------------------------------------------

  getObjectives(): ObjectiveProgress[] {
    const chapter = this.chapter;
    if (!chapter) return [];
    const list: ObjectiveProgress[] = [];
    let previousDone = true;
    for (const objective of chapter.objectives) {
      const done = evaluateCondition(objective.condition, this.state, this.world);
      // hidden な目標は、達成済みか、直前の目標が終わるまで伏せておく
      const visible = !objective.hidden || done || previousDone;
      list.push({ objective, done, visible });
      previousDone = done;
    }
    return list;
  }

  /** 手帳・HUD に出す「いま取り組むべき目標」 */
  getCurrentObjective(): ObjectiveProgress | null {
    return this.getObjectives().find((o) => !o.done) ?? null;
  }

  isChapterCleared(): boolean {
    const chapter = this.chapter;
    if (!chapter) return false;
    return evaluateCondition(chapter.clearCondition, this.state, this.world);
  }

  /**
   * クリア条件が満たされていれば章クリアを宣言する。
   * 同じ章で 2 回発火しないようにする。
   */
  checkChapterClear(): Chapter | null {
    const chapter = this.chapter;
    if (!chapter || this.clearedChapters.has(chapter.id)) return null;
    if (!this.isChapterCleared()) return null;

    this.clearedChapters.add(chapter.id);
    const stats = this.state.chapterStats[chapter.id];
    if (stats) stats.elapsedSec = Math.round((Date.now() - this.chapterStartedAt) / 1000);

    // 章クリアで確実に得られる知識カード
    this.runEffects(chapter.knowledgeCards.map<Effect>((cardId) => ({
      type: 'giveKnowledgeCard',
      cardId,
    })));

    this.events.emit('chapter-cleared', chapter);
    return chapter;
  }

  getDebriefDialogue(): Dialogue | null {
    const chapter = this.chapter;
    if (!chapter) return null;
    return this.dialoguesById[chapter.debriefDialogueId] ?? null;
  }

  // ------------------------------------------------------------
  // Effect の実行
  // ------------------------------------------------------------

  /** Effect 群を適用し、副作用（会話・音・カード）をイベントとして流す */
  runEffects(effects: Effect[] | undefined): EffectOutcome {
    const outcome = applyEffects(effects, this.state, this.world);
    this.dispatchOutcome(outcome);
    return outcome;
  }

  private dispatchOutcome(outcome: EffectOutcome): void {
    for (const clueId of outcome.newClues) this.events.emit('clue', clueId);
    for (const cardId of outcome.newCards) this.events.emit('card', cardId);
    for (const soundId of outcome.soundIds) this.events.emit('sound', soundId);
    for (const dialogueId of outcome.dialogueIds) this.events.emit('dialogue', dialogueId);
    if (outcome.changed) {
      this.events.emit('world-changed', undefined);
      this.events.emit('state-changed', undefined);
      this.noteProgress();
    }
  }

  // ------------------------------------------------------------
  // 調査対象
  // ------------------------------------------------------------

  getInteractable(id: string): Interactable | null {
    return this.interactablesById[id] ?? null;
  }

  get interactables(): Interactable[] {
    return this.data.interactables;
  }

  /** onInteract を上から評価し、最初に条件を満たしたものを実行する */
  interact(interactableId: string): { outcome: InteractionOutcome | null; effects: EffectOutcome } {
    const target = this.interactablesById[interactableId];
    if (!target) return { outcome: null, effects: emptyOutcome() };

    const chosen = target.onInteract.find((o) =>
      evaluateCondition(o.condition, this.state, this.world),
    );
    if (!chosen) return { outcome: null, effects: emptyOutcome() };

    const effects = this.runEffects(chosen.effects);
    if (chosen.description) {
      this.events.emit('description', { text: chosen.description, terms: chosen.terms });
    }
    this.noteProgress();
    return { outcome: chosen, effects };
  }

  // ------------------------------------------------------------
  // 会話
  // ------------------------------------------------------------

  getDialogue(id: string): Dialogue | null {
    return this.dialoguesById[id] ?? null;
  }

  /** once 指定の会話をすでに見たか */
  hasSeenDialogue(id: string): boolean {
    return this.state.seenDialogues.includes(id);
  }

  markDialogueSeen(id: string): void {
    if (!this.state.seenDialogues.includes(id)) {
      this.state.seenDialogues.push(id);
    }
  }

  markTermSeen(term: string): void {
    if (!this.state.seenTerms.includes(term)) {
      this.state.seenTerms.push(term);
      this.events.emit('state-changed', undefined);
    }
  }

  // ------------------------------------------------------------
  // 推理システム
  // ------------------------------------------------------------

  /** いま選択できる仮説（条件を満たしたもの） */
  getAvailableHypotheses(): Hypothesis[] {
    const chapter = this.chapter;
    if (!chapter) return [];
    return chapter.hypotheses.filter((h) => evaluateCondition(h.condition, this.state, this.world));
  }

  /** 仮説を選ぶ。間違っていても減点はせず、必ず学びの会話につなげる */
  chooseHypothesis(hypothesisId: string): { hypothesis: Hypothesis | null; dialogue: Dialogue | null } {
    const chapter = this.chapter;
    if (!chapter) return { hypothesis: null, dialogue: null };
    const hypothesis = chapter.hypotheses.find((h) => h.id === hypothesisId) ?? null;
    if (!hypothesis) return { hypothesis: null, dialogue: null };

    ensureChapterStats(this.state, chapter.id);
    if (!hypothesis.correct) {
      const stats = this.state.chapterStats[chapter.id];
      if (stats) stats.wrongHypotheses += 1;
    }
    this.state.flags[`hypothesis:${hypothesis.id}`] = true;
    this.noteProgress();
    this.events.emit('state-changed', undefined);

    return { hypothesis, dialogue: this.dialoguesById[hypothesis.dialogueId] ?? null };
  }

  isHypothesisChosen(hypothesisId: string): boolean {
    return this.state.flags[`hypothesis:${hypothesisId}`] === true;
  }

  /** 選んだ仮説によって解放された対処 ID の集合 */
  private unlockedFixIds(): Set<string> {
    const chapter = this.chapter;
    const unlocked = new Set<string>();
    if (!chapter) return unlocked;
    for (const hypothesis of chapter.hypotheses) {
      if (!this.isHypothesisChosen(hypothesis.id)) continue;
      for (const fixId of hypothesis.unlocksFixes ?? []) unlocked.add(fixId);
    }
    return unlocked;
  }

  /**
   * 選んだ仮説によって解放され、かつ condition を満たした対処。
   * UI が提示するリストであり、applyFix() の可否判定もこれと同じ基準で行う
   * （getFixBlockReason() と判定がずれないよう、ここも同じ 2 条件で絞る）。
   */
  getUnlockedFixes(): Fix[] {
    const chapter = this.chapter;
    if (!chapter) return [];
    return chapter.fixes.filter((fix) => this.getFixBlockReason(fix.id) === null);
  }

  /** 指定の調査対象を調べているときに実行できる対処 */
  getFixesForInteractable(interactableId: string): Fix[] {
    return this.getUnlockedFixes().filter(
      (fix) => !fix.requiresInteractable || fix.requiresInteractable === interactableId,
    );
  }

  /**
   * その対処をいま実行してよいか。
   *
   * - 選んだ仮説によって解放されているか（getUnlockedFixes と同じ判定）
   * - 対処自身の condition が満たされているか（＝順序の前提）
   *
   * どちらかが欠けていれば理由を返す。満たしていれば null。
   */
  getFixBlockReason(fixId: string): FixBlockReason | null {
    const chapter = this.chapter;
    const fix = chapter?.fixes.find((f) => f.id === fixId) ?? null;
    if (!fix) return 'not-found';

    if (!this.unlockedFixIds().has(fix.id)) return 'not-unlocked';
    if (!evaluateCondition(fix.condition, this.state, this.world)) return 'condition-not-met';
    return null;
  }

  canApplyFix(fixId: string): boolean {
    return this.getFixBlockReason(fixId) === null;
  }

  /**
   * 対処を実行する。
   *
   * 提示されていない対処（仮説で解放されていない／condition が偽）を
   * 直接呼ばれても効果を適用しない。
   * 以前はここに検査が無く、getUnlockedFixes()（UI が提示するリスト）でしか
   * 弾いていなかったため、applyFix('fix-ch2-renew') を直接呼べば
   * 私物ルーターを外す前に IP を取り直せてしまっていた。
   *
   * 弾いた場合は applied: false と blockedReason を返す（例外は投げない）。
   * fix 自体は「見つかったが実行できなかった」ことが分かるようそのまま返す。
   *
   * @param options.ignoreAvailability
   *   データ検証・デバッグ専用の抜け道。対処の効果そのものを世界に当てて
   *   検算したいときだけ使う。ゲーム本編（UI）からは決して使わない。
   */
  applyFix(fixId: string, options: { ignoreAvailability?: boolean } = {}): ApplyFixResult {
    const chapter = this.chapter;
    const fix = chapter?.fixes.find((f) => f.id === fixId) ?? null;
    if (!fix) {
      return { fix: null, effects: emptyOutcome(), applied: false, blockedReason: 'not-found' };
    }

    if (!options.ignoreAvailability) {
      const blockedReason = this.getFixBlockReason(fixId);
      if (blockedReason) {
        return { fix, effects: emptyOutcome(), applied: false, blockedReason };
      }
    }

    let effects = this.runEffects(fix.effects);
    if (fix.resultText) {
      this.events.emit('description', { text: fix.resultText });
    }
    effects = mergeOutcome(effects, emptyOutcome());
    this.noteProgress();
    return { fix, effects, applied: true, blockedReason: null };
  }

  // ------------------------------------------------------------
  // 手がかり・知識カード・用語
  // ------------------------------------------------------------

  getClues(): Clue[] {
    const chapter = this.chapter;
    if (!chapter) return [];
    return chapter.clues.filter((clue) => this.state.clues.includes(clue.id));
  }

  getAllChapterClues(): Clue[] {
    return this.chapter?.clues ?? [];
  }

  getKnowledgeCards(): KnowledgeCard[] {
    return this.state.knowledgeCards
      .map((id) => this.cardsById[id])
      .filter((card): card is KnowledgeCard => !!card);
  }

  getCard(id: string): KnowledgeCard | null {
    return this.cardsById[id] ?? null;
  }

  getGlossaryEntry(term: string): GlossaryEntry | null {
    const needle = term.trim();
    for (const entry of this.data.glossary) {
      if (entry.term === needle) return entry;
      if (entry.aliases?.includes(needle)) return entry;
    }
    return null;
  }

  get glossary(): GlossaryEntry[] {
    return this.data.glossary;
  }

  // ------------------------------------------------------------
  // ヒント
  // ------------------------------------------------------------

  /** いま出すべきヒント（未達成の条件に対応する最初のもの） */
  private getActiveHintIndex(): number {
    const chapter = this.chapter;
    if (!chapter) return -1;
    return chapter.hints.findIndex((hint) =>
      evaluateCondition(hint.condition, this.state, this.world),
    );
  }

  getHintState(): HintState {
    const chapter = this.chapter;
    const index = this.getActiveHintIndex();
    if (!chapter || index < 0) {
      return { available: false, revealed: 0, totalLevels: 0, texts: [], offered: false };
    }
    const hint = chapter.hints[index];
    const key = `${chapter.id}:${index}`;
    const revealed = this.hintRevealed.get(key) ?? 0;
    return {
      available: true,
      revealed,
      totalLevels: hint.levels.length,
      texts: hint.levels.slice(0, revealed),
      offered: this.idleSeconds >= HINT_IDLE_SECONDS,
    };
  }

  /** ヒントを 1 段階進める。プレイヤーの要求で呼ぶ */
  revealNextHint(): HintState {
    const chapter = this.chapter;
    const index = this.getActiveHintIndex();
    if (chapter && index >= 0) {
      const key = `${chapter.id}:${index}`;
      const hint = chapter.hints[index];
      const revealed = Math.min(hint.levels.length, (this.hintRevealed.get(key) ?? 0) + 1);
      this.hintRevealed.set(key, revealed);
      ensureChapterStats(this.state, chapter.id);
      const stats = this.state.chapterStats[chapter.id];
      if (stats) stats.hintsUsed += 1;
      this.idleSeconds = 0;
    }
    const hintState = this.getHintState();
    this.events.emit('hint', hintState);
    this.events.emit('state-changed', undefined);
    return hintState;
  }

  /** 毎フレーム呼ぶ。進展が無ければヒントを提案できる状態にする */
  tick(deltaSeconds: number): void {
    const signature = this.progressSignature();
    if (signature !== this.lastProgressSignature) {
      this.lastProgressSignature = signature;
      this.idleSeconds = 0;
      return;
    }
    const before = this.idleSeconds >= HINT_IDLE_SECONDS;
    this.idleSeconds += deltaSeconds;
    if (!before && this.idleSeconds >= HINT_IDLE_SECONDS) {
      this.events.emit('hint', this.getHintState());
    }
  }

  private progressSignature(): string {
    const objectives = this.getObjectives()
      .map((o) => (o.done ? '1' : '0'))
      .join('');
    return `${this.state.clues.length}:${Object.keys(this.state.flags).length}:${objectives}`;
  }

  private noteProgress(): void {
    this.idleSeconds = 0;
    this.lastProgressSignature = this.progressSignature();
  }

  // ------------------------------------------------------------
  // セーブ／ロード
  // ------------------------------------------------------------

  save(): boolean {
    const chapter = this.chapter;
    if (chapter) {
      ensureChapterStats(this.state, chapter.id);
      const stats = this.state.chapterStats[chapter.id];
      if (stats) stats.elapsedSec = Math.round((Date.now() - this.chapterStartedAt) / 1000);
    }
    return saveGame(this.state, this.world);
  }

  /** セーブデータを読み込む。成功すれば true */
  load(): boolean {
    const payload = loadGame();
    if (!payload) return false;
    this.state = payload.state;
    if (payload.world && typeof payload.world === 'object') {
      this.world = payload.world as NetworkWorld;
      migrateAddressPlan(this.world);
    } else {
      this.world = cloneWorld(this.pristineWorld);
    }
    this.clearedChapters.clear();
    this.hintRevealed.clear();
    this.chapterStartedAt = Date.now();
    this.events.emit('world-changed', undefined);
    this.events.emit('state-changed', undefined);
    return true;
  }

  /** 最初からやり直す */
  reset(playerName = this.state.playerName): void {
    this.world = cloneWorld(this.pristineWorld);
    const first = this.data.chapters[0];
    this.state = createInitialState(playerName, first ? first.id : '');
    this.clearedChapters.clear();
    this.hintRevealed.clear();
    this.idleSeconds = 0;
    this.events.emit('world-changed', undefined);
    this.events.emit('state-changed', undefined);
  }

  /**
   * プレイヤーが操作する端末の機器 ID（端末画面の「自分」）。
   * 慣例として 'pc_player' を使い、無ければスポーン地点の部屋にある PC、
   * それも無ければ最初の PC を採用する。
   */
  get playerDeviceId(): DeviceId {
    if (this.world.devices['pc_player']) return 'pc_player';
    const spawnRoom = this.data.world.layout?.spawn?.roomId;
    const ids = Object.keys(this.world.devices);
    const pcs = ids.filter((id) => this.world.devices[id].kind === 'pc');
    const inSpawnRoom = pcs.find((id) => this.world.devices[id].roomId === spawnRoom);
    return inSpawnRoom ?? pcs[0] ?? ids[0];
  }
}
